// Loan program content config, feeds shared LoanProgramPage template.
import {
  Banknote,
  ShieldCheck,
  Home,
  TreePine,
} from "lucide-react";

export const LOAN_PROGRAMS = {
  conventional: {
    slug: "conventional",
    name: "Conventional Loan",
    eyebrow: "Most popular",
    Icon: Home,
    headline: "The flexible path most buyers take.",
    subhead:
      "Put as little as 3% down with competitive rates, flexible terms, and a process built for buyers who want options.",
    heroImage:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
    benefits: [
      { title: "As little as 3% down", desc: "Don't wait years to save 20%. Start sooner." },
      { title: "No upfront mortgage insurance", desc: "Save on closing day vs. FHA loans." },
      { title: "Drop PMI at 20% equity", desc: "Your payment shrinks once you build equity." },
      { title: "Primary, second, or investment", desc: "Use it for the home you want to buy." },
    ],
    requirements: [
      { label: "Minimum credit score", value: "620" },
      { label: "Down payment", value: "3% +" },
      { label: "Debt-to-income", value: "Up to 45%" },
      { label: "Loan terms", value: "10 – 30 years" },
    ],
    bestFor:
      "Buyers with solid credit who want flexibility on property type and the smallest possible mortgage insurance cost.",
    faqs: [
      {
        q: "How much down do I really need?",
        a: "Conventional loans start at 3% down for first-time buyers. Putting 20% down lets you skip mortgage insurance entirely.",
      },
      {
        q: "Can I use a gift for my down payment?",
        a: "Yes, gift funds from family members are allowed on conventional loans. We'll guide you through the paperwork.",
      },
      {
        q: "How fast can I close?",
        a: "CityWorth conventional loans average 21 days from contract to keys.",
      },
    ],
  },

  fha: {
    slug: "fha",
    name: "FHA Loan",
    eyebrow: "First-time buyer friendly",
    Icon: ShieldCheck,
    headline: "Lower credit. Lower down payment. Open door.",
    subhead:
      "FHA loans are insured by the Federal Housing Administration, making homeownership accessible to buyers with lower credit scores and smaller savings.",
    heroImage:
      "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=80",
    benefits: [
      { title: "Just 3.5% down", desc: "Less to save means you can buy sooner." },
      { title: "Credit scores from 580", desc: "And as low as 500 with 10% down." },
      { title: "Generous DTI limits", desc: "Higher debt-to-income ratios accepted than most loans." },
      { title: "Gift funds welcome", desc: "100% of your down payment can be a gift." },
    ],
    requirements: [
      { label: "Minimum credit score", value: "580" },
      { label: "Down payment", value: "3.5% +" },
      { label: "Property", value: "Primary residence" },
      { label: "Mortgage insurance", value: "Required" },
    ],
    bestFor:
      "First-time buyers, those with limited savings, or anyone working to rebuild credit while still wanting to own a home.",
    faqs: [
      {
        q: "Is FHA only for first-time buyers?",
        a: "No, FHA is open to anyone who meets the requirements. It's just especially popular with first-time buyers.",
      },
      {
        q: "How long is FHA mortgage insurance?",
        a: "FHA loans require mortgage insurance for the life of the loan, but you can refinance to a conventional loan once you build equity.",
      },
      {
        q: "Do I have to live in the home?",
        a: "Yes, FHA loans are for owner-occupied primary residences only.",
      },
    ],
  },

  va: {
    slug: "va",
    name: "VA Loan",
    eyebrow: "For those who served",
    Icon: Banknote,
    headline: "$0 down. No mortgage insurance. Earned.",
    subhead:
      "Veterans, active-duty service members, and eligible spouses can buy a home with zero down and skip mortgage insurance entirely, a benefit you've earned.",
    heroImage:
      "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
    benefits: [
      { title: "$0 down payment", desc: "Keep your savings and buy with nothing down." },
      { title: "No mortgage insurance", desc: "Skip the monthly PMI most loans require." },
      { title: "Competitive interest rates", desc: "VA-backed loans often beat market rates." },
      { title: "No prepayment penalty", desc: "Pay it off early without fees." },
    ],
    requirements: [
      { label: "Service requirement", value: "Active / Vet / Spouse" },
      { label: "Down payment", value: "$0" },
      { label: "Certificate of Eligibility", value: "Required" },
      { label: "Property", value: "Primary residence" },
    ],
    bestFor:
      "Active military, veterans, National Guard or Reserves members, and eligible surviving spouses ready to put down roots.",
    faqs: [
      {
        q: "How do I get my Certificate of Eligibility?",
        a: "We help you get it. Most COEs are issued instantly through the VA's online portal.",
      },
      {
        q: "Can I use a VA loan more than once?",
        a: "Yes. VA loans are a lifetime benefit and can be used multiple times.",
      },
      {
        q: "What's the funding fee?",
        a: "A one-time fee that can be rolled into the loan. It's typically 1.4% – 3.6% depending on down payment and prior VA loan use.",
      },
    ],
  },

  usda: {
    slug: "usda",
    name: "USDA Loan",
    eyebrow: "Hidden gem",
    Icon: TreePine,
    headline: "$0 down. Rural and suburban areas.",
    subhead:
      "USDA loans help moderate-income buyers purchase homes in eligible rural and suburban areas with zero down and below-market rates.",
    heroImage:
      "https://images.unsplash.com/photo-1518780664697-55e3ad937233?auto=format&fit=crop&w=1200&q=80",
    benefits: [
      { title: "$0 down payment", desc: "Buy with nothing out of pocket." },
      { title: "Below-market rates", desc: "USDA backing keeps your rate competitive." },
      { title: "Lower mortgage insurance", desc: "Cheaper than FHA on a monthly basis." },
      { title: "Many areas qualify", desc: "Not just farmland. Many suburbs are eligible." },
    ],
    requirements: [
      { label: "Property location", value: "USDA-eligible area" },
      { label: "Down payment", value: "$0" },
      { label: "Recommended credit", value: "640 +" },
      { label: "Income", value: "Within program limits" },
    ],
    bestFor:
      "Buyers looking at smaller towns and suburbs who don't have a down payment saved and want a low monthly payment.",
    faqs: [
      {
        q: "How do I know if my area qualifies?",
        a: "We check the USDA eligibility map for you, many suburbs of major cities actually qualify.",
      },
      {
        q: "Is there an income limit?",
        a: "Yes, USDA has income caps that vary by household size and county. We confirm eligibility upfront.",
      },
      {
        q: "Do I have to be a first-time buyer?",
        a: "No, but the home must be your primary residence in an eligible area.",
      },
    ],
  },
};
